import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environment';

export interface AntennaLobeVisualizationSettings {
  minimum_boresight_length_m: number;
  maximum_boresight_length_m: number;
  maximum_ground_touch_length_m: number;
  minimum_power_dbm: number;
  maximum_power_dbm: number;
  horizontal_width_ratio: number;
  vertical_width_ratio: number;
  ground_clearance_m: number;
}

export interface AntennaLobeVisualizationSettingsResponse {
  settings: AntennaLobeVisualizationSettings;
}

export interface SimulationSettings {
  radio_map_frequency_ghz: number;
  radio_map_bandwidth_hz: number;
  radio_map_temperature_k: number;
  radio_map_receiver_noise_figure_db: number;
  radio_map_cell_size_m: number;
  radio_map_diffraction_enabled: boolean;
  radio_map_max_depth: number;
  radio_map_samples_per_tx: number;
  radio_map_plane_height_m: number;
  radio_map_min_extent_m: number;
}

export interface SimulationSettingsResponse {
  settings: SimulationSettings;
}

export interface StageAGuardrailRunpodServerlessSettings {
  enabled: boolean;
  max_estimated_total_vram_gb: number;
  pathsolver_complexity_limit: number;
  max_subscribers_no_batch: number;
  max_face_count_total: number;
  max_scene_dict_pickle_bytes: number;
  max_bbox_area_m2: number;
  recommended_subscriber_batch_size: number;
}

export interface StageAGuardrailSettings {
  policy_mode: 'deny';
  runpod_serverless: StageAGuardrailRunpodServerlessSettings;
}

export interface StageAGuardrailSettingsResponse {
  settings: StageAGuardrailSettings;
  settings_key: string;
  settings_source: string;
}

export interface SubscriberMarkerVisualizationSettings {
  asset_name: string;
  scale_x_m: number;
  scale_y_m: number;
  scale_z_m: number;
  show_all_by_default: boolean;
}

export interface SubscriberMarkerVisualizationSettingsResponse {
  settings: SubscriberMarkerVisualizationSettings;
}

export interface AvailableModelSummary {
  id: string;
  name: string;
  file: string;
  type: string;
  source: string;
}

export interface CellTowerDataSourcePurgeResponse {
  deleted: boolean;
  data_source: string;
  deleted_cell_towers: number;
}

export interface CellTowerDataSourcePurgeStatus {
  data_source: string;
  matching_cell_towers: number;
  can_purge: boolean;
  blocked_project_count: number;
  blocking_projects: Array<{
    id: number;
    name: string;
  }>;
}

export interface CensusUsIngestStateRequest {
  state: string;
  vintage?: 2020;
  unit_level?: 'block';
  batch_size?: number;
  source_name?: string;
}

export interface CensusUsIngestStateResponse {
  job_id: string;
  job_type: string;
}

export interface CensusAvailableSlice {
  state_fips: string;
  state_abbr: string;
  vintage: number;
  unit_level: string;
  row_count: number;
  population_total: number;
  latest_ingested_at?: string | null;
}

export type JobCleanupRetention = '1w' | '2w' | '1m';

export interface JobCleanupEstimate {
  backend_api_origin?: string;
  backend_scope?: 'local' | 'remote';
  request_host?: string;
  database_target?: string | null;
  retention: JobCleanupRetention;
  retention_label: string;
  older_than_days: number;
  cutoff_utc: string;
  aged_terminal_jobs: number;
  protected_referenced_jobs: number;
  projects_with_referenced_jobs?: number;
  referenced_baseline_jobs?: number;
  deletable_jobs: number;
  estimated_reclaimable_bytes: number;
  estimated_reclaimable_human: string;
  local_reclaimable_bytes?: number;
  local_reclaimable_human?: string;
  garage_reclaimable_bytes?: number;
  garage_reclaimable_human?: string;
  garage_configured?: boolean;
  garage_configuration_message?: string;
  garage_bucket?: string | null;
  garage_object_count?: number;
  existing_job_dirs: number;
  missing_job_dirs: number;
  sample_job_ids: string[];
  estimate_errors: Array<{
    job_id: string;
    error: string;
  }>;
}

export interface JobCleanupResult {
  backend_api_origin?: string;
  backend_scope?: 'local' | 'remote';
  request_host?: string;
  database_target?: string | null;
  deleted: boolean;
  deleted_jobs: number;
  deleted_job_dirs: number;
  deleted_from_db: number;
  reclaimed_bytes: number;
  reclaimed_human: string;
  local_reclaimed_bytes?: number;
  local_reclaimed_human?: string;
  garage_reclaimed_bytes?: number;
  garage_reclaimed_human?: string;
  garage_deleted_objects?: number;
  garage_configured?: boolean;
  retention: JobCleanupRetention;
  retention_label: string;
  older_than_days: number;
  protected_referenced_jobs?: number;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AppSettingsService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = environment.sionnaApiUrl;

  getAntennaLobeVisualizationSettings(): Observable<AntennaLobeVisualizationSettingsResponse> {
    return this.http.get<AntennaLobeVisualizationSettingsResponse>(
      `${this.apiBaseUrl}/visualization-settings/antenna-lobe`,
    );
  }

  updateAntennaLobeVisualizationSettings(
    settings: AntennaLobeVisualizationSettings,
  ): Observable<AntennaLobeVisualizationSettingsResponse> {
    return this.http.put<AntennaLobeVisualizationSettingsResponse>(
      `${this.apiBaseUrl}/visualization-settings/antenna-lobe`,
      settings,
    );
  }

  getSimulationSettings(): Observable<SimulationSettingsResponse> {
    return this.http.get<SimulationSettingsResponse>(
      `${this.apiBaseUrl}/visualization-settings/simulation`,
    );
  }

  updateSimulationSettings(settings: SimulationSettings): Observable<SimulationSettingsResponse> {
    return this.http.put<SimulationSettingsResponse>(
      `${this.apiBaseUrl}/visualization-settings/simulation`,
      settings,
    );
  }

  getStageAGuardrailSettings(): Observable<StageAGuardrailSettingsResponse> {
    return this.http.get<StageAGuardrailSettingsResponse>(
      `${this.apiBaseUrl}/runtime-settings/stage-a-guardrails`,
    );
  }

  updateStageAGuardrailSettings(
    settings: StageAGuardrailSettings,
  ): Observable<StageAGuardrailSettingsResponse> {
    return this.http.put<StageAGuardrailSettingsResponse>(
      `${this.apiBaseUrl}/runtime-settings/stage-a-guardrails`,
      settings,
    );
  }

  getSubscriberMarkerVisualizationSettings(): Observable<SubscriberMarkerVisualizationSettingsResponse> {
    return this.http.get<SubscriberMarkerVisualizationSettingsResponse>(
      `${this.apiBaseUrl}/visualization-settings/subscriber-marker`,
    );
  }

  updateSubscriberMarkerVisualizationSettings(
    settings: SubscriberMarkerVisualizationSettings,
  ): Observable<SubscriberMarkerVisualizationSettingsResponse> {
    return this.http.put<SubscriberMarkerVisualizationSettingsResponse>(
      `${this.apiBaseUrl}/visualization-settings/subscriber-marker`,
      settings,
    );
  }

  getAvailableModels(): Observable<AvailableModelSummary[]> {
    return this.http.get<AvailableModelSummary[]>(`${this.apiBaseUrl}/api/models`);
  }

  getCellTowerDataSourcePurgeStatus(dataSource: string): Observable<CellTowerDataSourcePurgeStatus> {
    return this.http.get<CellTowerDataSourcePurgeStatus>(
      `${this.apiBaseUrl}/api/cell-towers/data-sources/${encodeURIComponent(dataSource)}/purge-check`,
    );
  }

  purgeCellTowerDataSource(dataSource: string): Observable<CellTowerDataSourcePurgeResponse> {
    return this.http.post<CellTowerDataSourcePurgeResponse>(
      `${this.apiBaseUrl}/api/cell-towers/data-sources/${encodeURIComponent(dataSource)}/purge`,
      {},
    );
  }

  submitCensusUsIngestState(payload: CensusUsIngestStateRequest): Observable<CensusUsIngestStateResponse> {
    return this.http.post<CensusUsIngestStateResponse>(
      `${this.apiBaseUrl}/api/census/us/ingest-state`,
      payload,
    );
  }

  getAvailableCensusSlices(): Observable<CensusAvailableSlice[]> {
    return this.http.get<CensusAvailableSlice[]>(`${this.apiBaseUrl}/api/census/us/available`);
  }

  getJobsCleanupEstimate(retention: JobCleanupRetention): Observable<JobCleanupEstimate> {
    return this.http.get<JobCleanupEstimate>(`${this.apiBaseUrl}/api/admin/jobs/cleanup-estimate`, {
      params: { retention },
    });
  }

  cleanupOldJobs(retention: JobCleanupRetention): Observable<JobCleanupResult> {
    return this.http.post<JobCleanupResult>(`${this.apiBaseUrl}/api/admin/jobs/cleanup`, {}, {
      params: { retention },
    });
  }
}
